import type {
  UnifiedTableBlock,
  UnifiedTableRecord,
} from "../../../shared/ui/data-table/model/data-table-contract";

const summaryDownloadLinks = {
  csv: "#",
  xls: "#",
  pdf: "#",
};

const defaultTableUi = {
  searchable: true,
  searchPlaceholder: "Search...",
  downloadable: ["csv", "xls", "pdf"],
  showTableDownloads: true,
  downloadLinks: summaryDownloadLinks,
  defaultRowsPerPage: 10,
  rowsPerPageOptions: [5, 10, 20, 25],
};

const historyColumns = [
  {
    key: "dxTitle",
    header: "Dx Title",
    sortable: true,
    width: 280,
    rendererId: "wrappingCell",
  },
  { key: "dxCode", header: "Dx Code", sortable: true, width: 120 },
  { key: "dxStory", header: "Dx Story", sortable: true, width: 120 },
  { key: "onsetDate", header: "Onset Date", sortable: true, width: 140 },
  { key: "comments", header: "Comments", sortable: true, width: 180 },
  { key: "attributes", header: "Attributes", sortable: true, width: 150 },
  { key: "status", header: "Status", sortable: true, width: 120 },
];

const emptySectionTable = (
  id: string,
  message = "No data available",
): UnifiedTableBlock => ({
  id,
  columns: [{ key: "message", header: "" }],
  rows: [],
  ui: {
    showPaginator: false,
    emptyMessage: message,
    emptyStateVariant: "panel",
  },
});

export const summaryRecord: UnifiedTableRecord = {
  module: "summary",
  pageTitle: "Summary",
  subnav: [
    { sectionId: "problem-list", label: "Problem List" },
    { sectionId: "allergy-history", label: "Allergy History" },
    { sectionId: "family-history", label: "Family History" },
    { sectionId: "social-history", label: "Social History" },
    {
      sectionId: "health-maintenance-history",
      label: "Health Maintenance History",
    },
    { sectionId: "immunization-history", label: "Immunization History" },
    {
      sectionId: "pregnancy-birth-history",
      label: "Pregnancy & Birth History",
    },
    { sectionId: "other-past-history", label: "Other Past History" },
    { sectionId: "encounters", label: "Encounters" },
    { sectionId: "chart-attachments", label: "Chart Attachments" },
    { sectionId: "documents", label: "Documents" },
  ],
  sections: [
    {
      id: "problem-list",
      title: "Problem List",
      tables: [
        {
          id: "problem-list-table",
          columns: [
            {
              key: "dxTitle",
              header: "Dx Title",
              sortable: true,
              width: 420,
              rendererId: "wrappingCell",
            },
            { key: "icd9", header: "ICD9", sortable: true, width: 120 },
            { key: "icd10", header: "ICD10", sortable: true, width: 140 },
            { key: "dxStory", header: "Dx Story", sortable: true, width: 140 },
          ],
          rows: [
            {
              dxTitle:
                "Low back pain, unspecified, radiating into right leg or separate chronic low back pain episodes",
              icd9: "724.2",
              icd10: "M54.5",
              dxStory: "—",
            },
            {
              dxTitle: "Low back pain, episodic",
              icd9: "724.2",
              icd10: "M54.5",
              dxStory: "—",
            },
            {
              dxTitle: "Left hip pain",
              icd9: "719.45",
              icd10: "M25.552",
              dxStory: "—",
            },
            {
              dxTitle: "Hypertension",
              icd9: "401.9",
              icd10: "I10",
              dxStory: "—",
            },
            {
              dxTitle: "Hyperlipidemia",
              icd9: "272.4",
              icd10: "E78.5",
              dxStory: "—",
            },
            {
              dxTitle: "HTN (hypertension)",
              icd9: "401.9",
              icd10: "I10",
              dxStory: "—",
            },
          ],
          ui: {
            ...defaultTableUi,
            searchKeys: ["dxTitle", "icd9", "icd10", "dxStory"],
          },
        },
      ],
    },
    {
      id: "allergy-history",
      title: "Allergy History",
      tables: [
        {
          id: "allergy-history-table",
          columns: historyColumns,
          rows: [
            {
              dxTitle: "Allergies Reviewed",
              dxCode: "—",
              dxStory: "—",
              onsetDate: "—",
              comments: "—",
              attributes: "—",
              status: "Active",
            },
            {
              dxTitle: "Bactrim ANTI-INFECTIVE AGENTS - MISC.",
              dxCode: "—",
              dxStory: "—",
              onsetDate: "—",
              comments: "—",
              attributes: "—",
              status: "Active",
            },
            {
              dxTitle: "No Known Drug Allergies",
              dxCode: "—",
              dxStory: "—",
              onsetDate: "09/30/2022",
              comments: "—",
              attributes: "—",
              status: "Active",
            },
          ],
          ui: {
            ...defaultTableUi,
            searchKeys: historyColumns.map((column) => column.key),
          },
        },
      ],
    },
    {
      id: "family-history",
      title: "Family History",
      tables: [
        {
          id: "family-history-table",
          columns: historyColumns,
          rows: [
            {
              dxTitle: "No known family history",
              dxCode: "—",
              dxStory: "—",
              onsetDate: "—",
              comments: "—",
              attributes: "—",
              status: "Active",
            },
          ],
          ui: {
            ...defaultTableUi,
            searchKeys: historyColumns.map((column) => column.key),
          },
        },
      ],
    },
    {
      id: "social-history",
      title: "Social History",
      tables: [
        {
          id: "social-history-table",
          columns: historyColumns,
          rows: [
            {
              dxTitle: "Marital Status",
              dxCode: "—",
              dxStory: "—",
              onsetDate: "—",
              comments: "—",
              attributes: "Married",
              status: "Active",
            },
            {
              dxTitle: "Tobacco User",
              dxCode: "—",
              dxStory: "—",
              onsetDate: "—",
              comments: "10 yrs ago",
              attributes: "—",
              status: "Active",
            },
            {
              dxTitle: "Alcohol Use",
              dxCode: "—",
              dxStory: "—",
              onsetDate: "—",
              comments: "—",
              attributes: "Occasional",
              status: "Active",
            },
            {
              dxTitle: "Caffeine Use",
              dxCode: "—",
              dxStory: "—",
              onsetDate: "—",
              comments: "coffee",
              attributes: "—",
              status: "Active",
            },
          ],
          ui: {
            ...defaultTableUi,
            searchKeys: historyColumns.map((column) => column.key),
          },
        },
      ],
    },
    {
      id: "health-maintenance-history",
      title: "Health Maintenance History",
      tables: [emptySectionTable("health-maintenance-history-table")],
    },
    {
      id: "immunization-history",
      title: "Immunization History",
      tables: [
        {
          id: "immunization-history-table",
          columns: historyColumns,
          rows: [
            {
              dxTitle: "Immunization",
              dxCode: "—",
              dxStory: "—",
              onsetDate: "—",
              comments: "—",
              attributes: "—",
              status: "Active",
            },
          ],
          ui: {
            ...defaultTableUi,
            searchKeys: historyColumns.map((column) => column.key),
          },
        },
      ],
    },
    {
      id: "pregnancy-birth-history",
      title: "Pregnancy & Birth History",
      tables: [emptySectionTable("pregnancy-birth-history-table")],
    },
    {
      id: "other-past-history",
      title: "Other Past History",
      tables: [emptySectionTable("other-past-history-table")],
    },
    {
      id: "encounters",
      title: "Encounters",
      tables: [
        {
          id: "encounters-table",
          columns: [
            { key: "encCode", header: "Enc Code", sortable: true, width: 120 },
            {
              key: "visitType",
              header: "Visit Type",
              sortable: true,
              width: 200,
              rendererId: "wrappingCell",
            },
            { key: "encDate", header: "Enc Date", sortable: true, width: 140 },
            {
              key: "encDiagnosis",
              header: "Enc Diagnosis",
              sortable: true,
              width: 420,
              rendererId: "wrappingCell",
            },
            { key: "provider", header: "Provider", sortable: true, width: 170 },
          ],
          rows: [
            {
              encCode: "207802",
              visitType: "Physical Therapy Treatment",
              encDate: "11/29/2017",
              encDiagnosis:
                "FU: lumbar fusion, 2/2018, Low back pain unspecified, some numbness, unrelated to diagnosis",
              provider: "Testa, Nicholas",
            },
            {
              encCode: "186897",
              visitType: "Physical Therapy Treatment",
              encDate: "11/28/2017",
              encDiagnosis:
                "FU: lumbar fusion, 2/2018, Low back pain unspecified, some numbness, unspecified laterality",
              provider: "Quaranta, Chris",
            },
            {
              encCode: "164829",
              visitType: "Physical Therapy Treatment",
              encDate: "11/27/2017",
              encDiagnosis:
                "FU: lumbar fusion, 2/2018, Low back pain unspecified, chronic low back symptoms",
              provider: "Quaranta, Chris",
            },
            {
              encCode: "157750",
              visitType: "Physical Therapy Treatment",
              encDate: "11/22/2017",
              encDiagnosis:
                "FU: lumbar fusion, 2/2018, Low back pain unspecified, post-surgical rehab",
              provider: "Quaranta, Chris",
            },
          ],
          ui: {
            ...defaultTableUi,
            searchKeys: [
              "encCode",
              "visitType",
              "encDate",
              "encDiagnosis",
              "provider",
            ],
          },
        },
      ],
    },
    {
      id: "chart-attachments",
      title: "Chart Attachments",
      tables: [
        {
          id: "chart-attachments-table",
          columns: [
            { key: "type", header: "Type", sortable: true, width: 140 },
            { key: "date", header: "Date", sortable: true, width: 140 },
            { key: "name", header: "Name", sortable: true, width: 260 },
            { key: "provider", header: "Provider", sortable: true, width: 180 },
            {
              key: "message",
              header: "",
              rendererId: "chartAttachmentMessage",
              align: "right",
              width: 150,
            },
          ],
          rows: [
            {
              id: "attachment-1",
              type: "ENCOUNTER",
              date: "03/04/2022",
              name: "Gallbladder ultrasound",
              provider: "Green, Martin",
              message:
                "Lawsonia abstract retenteria - Julie Friedman, reviewed source note. Please see attached chart message for chart attachment details and source comments.",
            },
          ],
          ui: {
            ...defaultTableUi,
            searchKeys: ["type", "date", "name", "provider", "message"],
          },
        },
      ],
    },
    {
      id: "documents",
      title: "Documents",
      tables: [
        {
          id: "documents-table",
          columns: [
            {
              key: "documentName",
              header: "Document Name",
              sortable: true,
              width: 260,
            },
            {
              key: "documentType",
              header: "Document Type",
              sortable: true,
              width: 200,
            },
            {
              key: "scanDate",
              header: "Scan Date",
              sortable: true,
              width: 140,
            },
            {
              key: "fileType",
              header: "File Type",
              sortable: true,
              width: 140,
            },
            {
              key: "viewUrl",
              header: "",
              rendererId: "summaryDocumentView",
              align: "right",
              width: 100,
            },
          ],
          rows: [
            {
              documentName: "Blood Test Results",
              documentType: "Lab Report",
              scanDate: "12/31/2017",
              fileType: "PDF",
              viewUrl: "#",
            },
            {
              documentName: "X-Ray Chest",
              documentType: "Imaging Report",
              scanDate: "12/30/2017",
              fileType: "JPEG",
              viewUrl: "#",
            },
            {
              documentName: "Admission Form",
              documentType: "Hospital Chart",
              scanDate: "12/30/2017",
              fileType: "JPEG",
              viewUrl: "#",
            },
            {
              documentName: "Digestive System - Sequences",
              documentType: "External information",
              scanDate: "08/26/2017",
              fileType: "TIFF",
              viewUrl: "#",
            },
            {
              documentName: "Dental - Signed Records",
              documentType: "External information",
              scanDate: "02/25/2017",
              fileType: "TIFF",
              viewUrl: "#",
            },
            {
              documentName: "Finance Ledger",
              documentType: "Financial Policy",
              scanDate: "02/12/2017",
              fileType: "TXT",
              viewUrl: "#",
            },
          ],
          ui: {
            ...defaultTableUi,
            searchKeys: [
              "documentName",
              "documentType",
              "scanDate",
              "fileType",
            ],
          },
        },
      ],
    },
  ],
};
